import React, { useEffect, useState, Suspense, FC } from 'react';
import { t, Trans } from '@lingui/macro';
import { Field, Formik, FormikProps, FormikHelpers as FormikActions } from 'formik';
import {
  Text,
  Box,
  SideSheet,
  SideSheetSize,
  SideSheetFooter,
  SideSheetBody,
  SideSheetHeader,
  SideSheetClose,
  SideSheetContentCol,
  Spinner,
  ButtonAppearance,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';

import { ApolloError } from '@apollo/client';
import { SectionHeader, FooterButtonStyled, ErrorBoundary, SuspenseLoader, useAppUser } from '@bechtel/client-common';
import { FormikState, HourEntryInput, HourCode, TimesheetReasons } from './types';
import { CrewMembersListStyled, FooterStyled, WarningContainerStyled } from './styled';
import { array, object, string, number } from 'yup';
import { TimeEntryInput, SaveTimesheetInput, SaveTimesheetChargesError, Crew, PersonaType } from '@bechtel/types';
import { every, forEach, get, toNumber, findIndex, pullAt, concat } from 'lodash';
import { useSaveTimeSheet } from '~/hooks';
import { formatISO, format } from 'date-fns';
import { CostCodes } from './cost-codes';
import { SaveTimeSheetError } from '~/components/organisms';
import { CrewEmployeeList } from './crew-member';
import { TimesheetEntry } from './time-entry-form';
import { useTimesheet } from '~/detail/context';
import { getTimeEntryInputListFromCrew } from '~/detail/utils';
import { useParams, useLocation } from 'react-router-dom';

interface BulkTimesheetProps {
  onClose: () => void;
  timesheetParsedValues: any;
}
export const BulkTimesheet: FC<BulkTimesheetProps> = ({ onClose, timesheetParsedValues }) => {
  const dispatchToast = useToastsDispatch();
  const { forDate: selectedDate, crewEmployees: crewMemberList } = useTimesheet();
  /**TODO:Left side list not ready will remove after base save mutation setup BUT in android they took from authbyuser */
  const { user } = useAppUser();
  const { id } = useParams();
  const { state } = useLocation();
  const foremanId =
    user?.crewMember.persona === PersonaType.FOREMAN ? user?.crewMember.id : id ? decodeURIComponent(id) : undefined;
  const userCrew: Crew = state ? state : user?.crewMember?.crew;

  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const [saveTimeSheetError, setSaveTimeSheetError] = useState<SaveTimesheetChargesError[] | ApolloError | null>(null);
  const { timesheetId, crewId, secheduleHourTypeList, duration, roundingIncrement, timesheetDayCrewMembers } =
    timesheetParsedValues;
  const [saveTimeSheet, { loading: mutationLoading, error: mutationError, data: mutationData }] = useSaveTimeSheet(
    () => {
      setActionCompleted(true);
    }
  );
  const UpdatedTimeSheetToast = () => {
    return (
      <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        <Text family="graphik" weight="600" size="500">
          <Trans>Time sheet updated successfully.</Trans>
        </Text>
      </Box>
    );
  };
  useEffect(() => {
    if (!actionCompleted) return;
    const error = get(mutationData, 'saveTimesheet.errors', '') || mutationError;
    if (!error) {
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: UpdatedTimeSheetToast,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
    } else {
      setSaveTimeSheetError(error);
    }
    setActionCompleted(false);
  }, [actionCompleted, dispatchToast, mutationData, mutationError, onClose]);

  const hourTypesList: HourEntryInput[] = [];
  forEach(secheduleHourTypeList, (secheduleHourType) => {
    hourTypesList.push({
      id: secheduleHourType?.id,
      maxHours: secheduleHourType.maxHours == 0 ? (duration == 0 ? 24 : duration) : secheduleHourType.maxHours,
      description:
        secheduleHourType.hourCode === HourCode.ST
          ? t`Straight Time Hours`
          : secheduleHourType.hourCode === HourCode.OT
          ? t`Over Time Hours`
          : secheduleHourType.hourCode === HourCode.DT
          ? t`Double Time Hours`
          : secheduleHourType.description,
      hourCode: secheduleHourType.hourCode,
      hours: undefined,
      reason: null,
      otherCommentDescription: null,
      roundingIncrement: roundingIncrement,
    });
  });

  const initialValues: FormikState = {
    costCode: null,
    crewEmployees: crewMemberList,
    selectedCrewEmployees: [],
    enteredHourTypes: hourTypesList,
  };

  const UpdateCrewTimeValidationSchema = object().shape({
    costCode: object().required(t`Cost code is mandatory`),
    selectedCrewEmployees: array()
      .required()
      .min(1, t`Select at least 1 crew member`),
    enteredHourTypes: array()
      .of(
        object().shape({
          hours: number()
            .min(0, t`Hours should not be in minus`)
            .when(
              'maxHours',
              (maxHours, yup) => maxHours && yup.max(maxHours, t`Hours should not be greater than ${maxHours} .`)
            )
            .typeError(t`Hours must be a number`)
            .nullable(),
          otherCommentDescription: string()
            .nullable()
            .notRequired()
            .when('reason', {
              is: (reason) => reason && reason?.name === TimesheetReasons.TIMESHEET_REASON_OTHER,
              then: string()
                .required(t`Comment is mandatory`)
                .nullable(),
            }),
        })
      )
      .test({
        name: 'hours',
        message: 'Required',
        test: (val) => !every(val, ['hours', undefined]),
      }),
  });

  const closeModal = () => {
    setSaveTimeSheetError(null);
  };

  return (
    <SideSheet
      visible
      style={{
        boxShadow: `0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14)`,
      }}
      size={SideSheetSize.MEDIUM}
    >
      <SideSheetHeader title={t`Bulk Time Input`} data-testid="bulk-timesheet-header">
        <Box justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }} mt={{ sm: '300' }}>
          {selectedDate && (
            <Text family="graphik" size="400">
              {format(selectedDate, 'EEEE, LLL d')}
            </Text>
          )}
        </Box>
        <SideSheetClose onClick={() => onClose()} disabled={mutationLoading} />
      </SideSheetHeader>

      <Formik
        initialValues={initialValues}
        validationSchema={UpdateCrewTimeValidationSchema}
        onSubmit={async (values: FormikState, actions: FormikActions<FormikState>) => {
          if (!values) return;
          actions.setSubmitting(true);
          const existingTimeEntryInputs = getTimeEntryInputListFromCrew(timesheetDayCrewMembers);
          const timeEntryInput: TimeEntryInput[] = [];
          const indexesToRemove: number[] = [];
          forEach(values.enteredHourTypes, (hoursType) => {
            forEach(values.selectedCrewEmployees, (crewEmployee) => {
              const foundIndex = findIndex(
                existingTimeEntryInputs,
                (timeEntryInput) =>
                  timeEntryInput.crewMemberId == crewEmployee.id &&
                  timeEntryInput.hourCode == hoursType.hourCode &&
                  timeEntryInput.costCodeId == values.costCode?.id
              );
              if (foundIndex !== -1) indexesToRemove.push(foundIndex);
              timeEntryInput.push({
                id: foundIndex !== -1 ? existingTimeEntryInputs[foundIndex].id : undefined,
                hourCode: hoursType.hourCode,
                commentId: hoursType.reason ? hoursType.reason.id : '',
                otherCommentDescription: hoursType.otherCommentDescription?.trim()
                  ? hoursType.otherCommentDescription
                  : '',
                hours: hoursType?.hours ? toNumber(hoursType.hours) : 0,
                crewMemberId: crewEmployee.id,
                costCodeId: values.costCode?.id ? values.costCode?.id : '',
              });
            });
          });
          if (indexesToRemove.length > 0) pullAt(existingTimeEntryInputs, indexesToRemove);

          const saveTimesheetInput: SaveTimesheetInput = {
            timesheetId: timesheetId,
            crewId: crewId,
            forDate: formatISO(selectedDate, { representation: 'date' }),
            timeEntryInput: concat(existingTimeEntryInputs, timeEntryInput),
          };

          await saveTimeSheet({
            variables: {
              input: saveTimesheetInput,
            },
          });
          actions.setSubmitting(false);
          actions.resetForm();
        }}
      >
        {({ isValid, dirty, handleSubmit }: FormikProps<FormikState>) => {
          return (
            <>
              <SideSheetBody data-testid="time-Sheet-Body">
                <CrewMembersListStyled>
                  <Box p={{ sm: '550' }}>
                    <Text weight="600" size="400" color="grey.1000">
                      <Trans>Work Package:</Trans>
                    </Text>
                    &nbsp;
                    <Text size="400" color="grey.600">
                      <Trans>Non Work Package Cost Code</Trans>
                    </Text>
                  </Box>
                  <Box flexDirection={{ sm: 'column' }} data-testid="cost-Code-dropdown">
                    <SectionHeader title={t`Select Cost Code`} />
                    <CostCodes
                      name="costCode"
                      crewId={userCrew?.id ? userCrew.id : ''}
                      crewMemberId={foremanId}
                      isDisabled={mutationLoading}
                    />
                  </Box>
                  <SectionHeader title={t`Select Crew Members`} />
                  <SideSheetContentCol data-testid="crew-Employees">
                    <Field name="crewEmployees" component={CrewEmployeeList} />
                  </SideSheetContentCol>
                </CrewMembersListStyled>
                <SideSheetContentCol data-testid="entered-Hour-Types">
                  <Field name="enteredHourTypes" component={TimesheetEntry} />
                </SideSheetContentCol>
              </SideSheetBody>
              <SideSheetFooter>
                <FooterStyled>
                  <Box alignItems={{ sm: 'center' }}>
                    {dirty && (
                      <WarningContainerStyled>
                        <Text weight="600" size="400" color="grey.100" family="graphik">
                          <Trans>Note:</Trans>
                        </Text>
                        &nbsp;
                        <Text color="grey.100" size="400" family="graphik">
                          <Trans>
                            All time entries for this charge code will be overwritten for these crew members.
                          </Trans>
                        </Text>
                      </WarningContainerStyled>
                    )}
                  </Box>

                  <Box justifyContent={{ sm: 'flex-end' }} alignItems={{ sm: 'center' }}>
                    <Box mr={{ sm: '400' }}>
                      <FooterButtonStyled
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={() => onClose()}
                        disabled={mutationLoading}
                      >
                        <Trans>Cancel</Trans>
                      </FooterButtonStyled>
                    </Box>
                    <FooterButtonStyled
                      onClick={() => handleSubmit()}
                      appearance={ButtonAppearance.SECONDARY}
                      disabled={mutationLoading || !dirty || !isValid}
                    >
                      {mutationLoading && <Spinner />}
                      {!mutationLoading && <Trans>Update Time</Trans>}
                    </FooterButtonStyled>
                  </Box>
                </FooterStyled>
              </SideSheetFooter>
            </>
          );
        }}
      </Formik>

      {saveTimeSheetError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <SaveTimeSheetError errors={saveTimeSheetError} crewMemberList={crewMemberList} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </SideSheet>
  );
};
